import {
    InternalAssessmentApiModel,
    InternalAssessmentApiRow,
    InternalAssessmentTab,
    internalAssessmentTabFromQuery,
    internalAssessmentTabToQuery,
} from "./InternalAssessments.types";
import { LegalEntityAuditStatus } from "../legalEntity/legalEntityAuditTab/LegalEntityAudit.types";
import { RiskLevel } from "../legalEntity/legalEntityClassificationTab/LegalEntityClassificationTab.types";
import { IdNamePair } from "../customLists/customListView/CustomListView.types";

// GET /internalAssessments returns one payload covering every assessment, and its three selectable lists are
// computed across all of them. Each view shows a subset of the rows, so the lists are narrowed to what those rows
// actually reference, otherwise a filter dropdown offers values that can never match a row in the current view.
export function toViewModel(
    data: InternalAssessmentApiModel | undefined,
    includeRow: (row: InternalAssessmentApiRow) => boolean
): InternalAssessmentApiModel | undefined {
    if (!data) {
        return undefined;
    }

    const rows = data.rows.filter(includeRow);
    const questionnaireIds = new Set(rows.map((row) => row.questionnaireId));
    const responsibleIds = new Set(rows.map((row) => row.responsible.id));
    const complianceAreaIds = new Set(rows.flatMap((row) => row.complianceAreas.map((area) => area.id)));

    return {
        rows,
        selectableAssessmentTemplate: data.selectableAssessmentTemplate.filter((x) => questionnaireIds.has(x.id)),
        selectableResponsibles: data.selectableResponsibles.filter((x) => responsibleIds.has(x.id)),
        selectableComplianceAreas: data.selectableComplianceAreas.filter((x) => complianceAreaIds.has(x.id)),
    };
}

/**
 * The risk level as the table's risk cell renders it, so the risk filter and the column stay in step. Mirrors
 * QuestionnaireRisk: nothing shows until the assessment is submitted, a template without scoring reads as "none",
 * and a submitted assessment still missing its level shows a warning icon rather than a level.
 */
export function getDisplayedRiskLevel(row: InternalAssessmentApiRow, translateString: (key: string) => string): IdNamePair | undefined {
    if (row.status !== LegalEntityAuditStatus.Submitted) {
        return undefined;
    }

    if (!row.scoreEnabled) {
        return { id: RiskLevel.None.toString(), name: translateString("none") };
    }

    if (row.riskLevel === undefined || !row.riskName) {
        return undefined;
    }

    return { id: row.riskLevel.toString(), name: row.riskName };
}

const viewStorageKey = "internal-assessments-view";

export function rememberInternalAssessmentTab(tab: InternalAssessmentTab) {
    localStorage.setItem(viewStorageKey, internalAssessmentTabToQuery(tab));
}

// There is a single internal assessments page, so the remembered view is read as the hook's default rather than
// baked into the nav menu link the way a risk area does. That covers every way back onto the page, since the nav
// menu, the start page redirect and a typed url all arrive without a ?view=.
export function readRememberedInternalAssessmentTab(): InternalAssessmentTab {
    return internalAssessmentTabFromQuery(localStorage.getItem(viewStorageKey));
}
